import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { authService } from '../services/auth';

const AGENT_DOCS = [
  { title: 'Cursor', slug: 'cursor', summary: 'Override OpenAI base URL in Cursor settings.' },
  { title: 'Claude Code', slug: 'claude-code', summary: 'Connect via Anthropic gateway settings.' },
  { title: 'Codex', slug: 'codex', summary: 'Set openai_base_url in ~/.codex/config.toml.' },
  { title: 'OpenClaw', slug: 'openclaw', summary: 'Point your agent at the osrouter API.' },
];

function DocCard({ title, summary, href }) {
  const className =
    'block p-4 rounded-xl border border-theme-secondary hover:border-theme-primary/40 transition-colors';
  const content = (
    <>
      <div className="font-medium text-theme-primary">{title}</div>
      <div className="text-sm opacity-70 mt-1">{summary}</div>
    </>
  );

  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {content}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {content}
    </Link>
  );
}

export default function DocsPage() {
  const [env, setEnv] = useState(null);

  useEffect(() => {
    authService.fetchUser((res) => setEnv(res.data.env || {}), console.error);
  }, []);

  const landingBase = (env?.landing_page_url || '').replace(/\/$/, '');

  const sections = useMemo(() => {
    if (!landingBase) return [];

    return [
      {
        title: 'Models',
        items: [
          {
            title: 'Model catalog',
            summary: 'Browse routed models, tiers, and pricing in the app.',
            href: '/docs/models/',
          },
        ],
      },
      {
        title: 'API',
        items: [
          {
            title: 'API reference',
            summary: 'OpenAI-compatible Chat Completions endpoints and auth.',
            href: `${landingBase}/docs/api/`,
          },
        ],
      },
      {
        title: 'Agents',
        items: AGENT_DOCS.map((doc) => ({
          title: doc.title,
          summary: doc.summary,
          href: `${landingBase}/docs/agents/${doc.slug}/`,
        })),
      },
    ];
  }, [landingBase]);

  return (
    <DashboardLayout title="Docs">
      {!landingBase ? (
        <p className="text-sm opacity-70">Loading documentation links…</p>
      ) : (
        <div className="space-y-10 max-w-3xl">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-sm font-medium opacity-60 uppercase tracking-wider mb-4">
                {section.title}
              </h2>
              <div className="grid gap-4">
                {section.items.map((item) => (
                  <DocCard key={item.href} {...item} />
                ))}
              </div>
            </section>
          ))}
          {env?.api_url && (
            <p className="text-sm opacity-70">
              Production API base URL:{' '}
              <code className="text-theme-primary">{env.api_url}/api/v1</code>
            </p>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
